import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { createSelector } from "reselect";
import { connect } from "react-redux";
import queryString from "query-string";
import { Table } from "workbench";
import Breadcrumb from "../../../common/BreadCrumb";
import {
  bindDispatch,
  checkScopes,
  emptyDataMessage,
  formatDateTime,
  modifyTableColumns,
} from "../../../../utils";
import PageHeader from "../../../common/PageHeader";
import TableControls from "../../../common/TableControls";
import withFetchList from "../../../hoc/withFetchList";
import TableLoading from "../../../common/TableLoading";
import pageStyles from "../../../../styles/App.module.scss";
import * as constants from "../../../../constants";
import FilterChips from "../../../common/Filters/FilterChips";
import UserTableAccessor from "../../../common/UserTableAccessor";
import FooterControls from "../../../common/FooterControls";
import { ENTITY_TYPE_LIST } from "../../../../constants/campaigns";
import { CORE_SYSTEM_ID } from "../../../../config";
import CampaignLogsView from "./CampaignLogsView";

const _LINKS = [{ name: "PageHeader.campaigns", path: "/campaigns" }];
const SelectedColumns = [
  "Entity Name",
  "Entity Type",
  "Action",
  "Updated On",
  "Updated By",
  "Changes",
];
const CampaignEditLogs = ({ ...props }) => {
  const {
    history,
    campaigns,
    actions,
    match,
    filters,
    sort,
    ps,
    page,
    onSortedChange,
    onQueryChange,
    onFilterSelect,
    onFilterChange,
    onFilterIdSelect,
    fetchData,
    campaignLogs,
  } = props;
  const { campaign, isLoading } = campaigns;
  const { data, isLoading: isCampaignLogsLoading } = campaignLogs;

  const [selectedColumns, setSelectedColumns] = useState(SelectedColumns);
  const [reorderedColumns, setReorderedColumns] = useState(SelectedColumns);
  useEffect(() => {
    const fetchCampaign = async () => {
      if (match.params.campaignId) {
        await actions.getCampaign(match.params);
        await fetchData();
      }
    };
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onColumnSelect = (selectedColumns, reorderedColumns) => {
    setSelectedColumns(selectedColumns);
    setReorderedColumns(reorderedColumns);
  };

  const columns = checkScopes([
    {
      id: "entityName",
      Header: "Entity Name",
      accessor: (d) => d.entityName,
      minWidth: 200,
    },
    {
      id: "entityType",
      Header: "Entity Type",
      accessor: (d) =>
        ENTITY_TYPE_LIST.find((item) => item.id === d.entityType)?.displayName || d.entityType,
      minWidth: 200,
    },
    {
      id: "action",
      Header: "Action",
      accessor: (d) => {
        if (d.action === "campaign.clearPaused") {
          return "Pause Cleared";
        } else if (d.action === "campaign.clearCancelled") {
          return "Cancellation Cleared";
        }
        const [, action] = d.action.split(".");
        // Add space between camel case letters
        const formatted = action.replace(/([a-z])([A-Z])/g, "$1 $2");
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
      },
      minWidth: 200,
    },
    {
      id: "updatedAt",
      Header: "Updated On",
      accessor: (d) => formatDateTime(d.updatedAt, "DD MMM YYYY hh:mm A"),
      minWidth: 200,
    },
    {
      id: "updatedBy",
      Header: "Updated By",
      accessor: (d) => (
        <div>
          {d?.updatedBy?.id === CORE_SYSTEM_ID ? "System" : ""}
          {UserTableAccessor("updatedBy", onFilterIdSelect)(d)}
          <div style={{ fontSize: "0.9em", color: "#666" }}>{d.updatedBy.email?.toLowerCase()}</div>
        </div>
      ),
      minWidth: 200,
    },
    {
      id: "changes",
      Header: "Changes",
      accessor: (d) => {
        return <CampaignLogsView editLog={d} campaignId={campaign.id} />;
      },
      minWidth: 200,
    },
  ]);
  if (!campaign) {
    return null;
  }
  const resultsCount = data?.totalCount || 0;
  const query = queryString.parse(history.location.search);

  return (
    <div className="col-12 clearfix">
      <div className="col-12 clearfix">
        <Breadcrumb
          isLoading={isLoading}
          history={history}
          links={[
            ..._LINKS,
            {
              name: `${campaign.name}`,
              path: `/campaigns/campaigns/${match.params.campaignId}/details`,
            },
            { name: "PageHeader.editLogs" },
          ]}
        />
      </div>
      <PageHeader
        name={`Logs: ${campaign.name}`}
        isLoading={isLoading}
        PageHeader={"PageHeader.editLogs"}
      />
      <div
        className={classNames("col-12 clearfix", pageStyles.pageWrapper, pageStyles.pageContainer)}
      >
        <TableControls
          searchBar
          columnFilter
          showRowSize
          pagination
          columns={columns}
          selectedColumns={selectedColumns}
          reorderedColumns={reorderedColumns}
          data={data}
          query={query}
          ps={ps}
          page={page}
          tagTypes={[
            constants.TAG_TYPE.ENTITY_NAME,
            constants.TAG_TYPE.ENTITY_TYPE,
            constants.TAG_TYPE.LOG_ACTION,
            constants.TAG_TYPE.USER,
          ]}
          onSearchFilterSelect={onFilterSelect}
          onFilterChange={onFilterChange}
          onColumnFilterSelect={onColumnSelect}
          onRowSizeChange={onQueryChange}
          filters={filters}
        />
        <FilterChips
          selected={filters}
          showResultsCount
          resultsCount={resultsCount}
          onFilterChange={onFilterChange}
        />
        <Table
          data={data?.data}
          // defaultSortDesc={true}
          columns={modifyTableColumns(columns, selectedColumns)}
          manual
          loading={isCampaignLogsLoading}
          sorted={sort}
          onSortedChange={onSortedChange}
          LoadingComponent={<TableLoading columns={columns} />}
          emptyDataMessage={emptyDataMessage(
            `EmptyDataMessage.noCampaignLogsFound`,
            `EmptyDataMessage.noCampaignLogsFoundBody`
          )}
          // onRowClick={(props) =>
          //   history.push(
          //     `/brand-verifications/${props.original.parentBrandId}/${props.original.submittedBy.company.id}`
          //   )
          // }
        />
        <FooterControls
          pagination
          data={data}
          query={query}
          ps={ps}
          page={page}
          onRowSizeChange={onQueryChange}
        />
      </div>
    </div>
  );
};

const CampaignEditLogsWithFileters = withFetchList("getCampaignLogs", {
  sort: [{ id: "updatedAt", desc: true }],
})(CampaignEditLogs);

const mapStateToProps = createSelector(
  (state) => state.campaigns,
  (state) => state.userData,
  (state) => state.media,
  (state) => state.campaignLogs,
  (campaigns, userData, media, campaignLogs) => ({
    campaigns,
    userData: userData.user,
    media,
    campaignLogs,
  })
);

export default connect(mapStateToProps, bindDispatch)(CampaignEditLogsWithFileters);
